import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ConceptFlow from "../components/ConceptFlow";
import { GLOSSAR } from "../lib/glossar";
import { usePerspective } from "../lib/perspective";
import { KUNDE_ALLOW } from "../components/Shell";
import {
  AGENTEN,
  AUDIENCE_LABEL,
  FAQ,
  FUNKTIONSSTAND,
  MELDEWEG,
  MODULE_HELP,
  PORTALE,
  ROLLEN_GUIDES,
  ROLLEN_HINWEISE,
  ROLLEN_MATRIX,
  SYSTEME,
  type HelpAudience,
  type HelpImage,
  type ModulHelp,
} from "../lib/helpContent";

type RoleFilter = "alle" | HelpAudience;

const STATUS_LABEL: Record<"live" | "teilweise" | "geplant", string> = {
  live: "aktiv",
  teilweise: "teilweise",
  geplant: "nicht aktiv",
};

function normalize(value: string) {
  return value.toLocaleLowerCase("de-DE").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function modulHaystack(m: ModulHelp): string {
  return normalize(
    [m.titel, m.kurz, m.hintergrund, m.daten, m.rollen, m.grenzen ?? "", ...m.sehen, ...m.tun, ...m.tags].join(" "),
  );
}

/** Statische Wirkungs-Landkarte: Quellen -> geprüfte Datenbasis -> Arbeitsflächen -> Mensch. */
function WirkungsLandkarte() {
  const box = "help-map-box";
  const label = "help-map-label";
  const sub = "help-map-sub";
  return (
    <svg
      viewBox="0 0 960 400"
      role="img"
      aria-labelledby="help-map-title help-map-desc"
      className="help-map-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <title id="help-map-title">Wirkungs-Landkarte der Plattform</title>
      <desc id="help-map-desc">
        Quellsysteme links (OpenProject, Nextcloud, Meetily, Kennzahlen-Lieferungen) fließen in die geprüfte
        Datenbasis (Bronze, Silver, Gold mit Qualitäts-Gate). Daraus lesen diese Anwendung und die Chatbots.
        Menschen entscheiden; zurück in die Quellsysteme wird nur mit ausdrücklicher Freigabe geschrieben.
      </desc>
      {/* Quellen */}
      <g>
        <rect x="20" y="24" width="200" height="64" rx="8" className={box} />
        <text x="120" y="50" textAnchor="middle" className={label}>OpenProject</text>
        <text x="120" y="70" textAnchor="middle" className={sub}>Arbeitspakete · täglich 05:30</text>

        <rect x="20" y="112" width="200" height="64" rx="8" className={box} />
        <text x="120" y="138" textAnchor="middle" className={label}>Nextcloud</text>
        <text x="120" y="158" textAnchor="middle" className={sub}>Dokumente · wenige Stunden</text>

        <rect x="20" y="200" width="200" height="64" rx="8" className={box} />
        <text x="120" y="226" textAnchor="middle" className={label}>Meetily</text>
        <text x="120" y="246" textAnchor="middle" className={sub}>nur freigegebene Ableitung</text>

        <rect x="20" y="288" width="200" height="64" rx="8" className={box} />
        <text x="120" y="314" textAnchor="middle" className={label}>Kennzahlen-Lieferungen</text>
        <text x="120" y="334" textAnchor="middle" className={sub}>fester Datenvertrag · :05/:35</text>
      </g>
      {/* Pfeile Quellen -> Lakehouse */}
      <g className="help-map-arrow">
        <path d="M220 56 H272 Q284 56 288 92 L292 150" />
        <path d="M220 144 H288" />
        <path d="M220 232 H272 Q284 232 288 200 L292 190" />
        <path d="M220 320 H272 Q284 320 290 250 L293 210" />
      </g>
      {/* Lakehouse */}
      <g>
        <rect x="300" y="96" width="220" height="180" rx="10" className={`${box} help-map-core`} />
        <text x="410" y="126" textAnchor="middle" className={label}>Data Lakehouse</text>
        <text x="410" y="150" textAnchor="middle" className={sub}>Bronze → Silver → Gold</text>
        <text x="410" y="172" textAnchor="middle" className={sub}>automatische Prüfungen (dbt)</text>
        <rect x="322" y="192" width="176" height="34" rx="6" className="help-map-gate" />
        <text x="410" y="214" textAnchor="middle" className={sub}>Qualitäts-Gate: PASS · REVIEW · BLOCK</text>
        <text x="410" y="256" textAnchor="middle" className={sub}>Gold = geprüfte Wahrheit</text>
      </g>
      {/* Pfeile Lakehouse -> Flächen */}
      <g className="help-map-arrow">
        <path d="M520 150 H600" />
        <path d="M520 226 H600" />
      </g>
      {/* Arbeitsflächen */}
      <g>
        <rect x="608" y="110" width="220" height="80" rx="8" className={box} />
        <text x="718" y="140" textAnchor="middle" className={label}>Diese Anwendung</text>
        <text x="718" y="160" textAnchor="middle" className={sub}>Cockpits · Module · Briefings</text>
        <text x="718" y="178" textAnchor="middle" className={sub}>„Frag die Daten" · Advisor</text>

        <rect x="608" y="210" width="220" height="64" rx="8" className={box} />
        <text x="718" y="238" textAnchor="middle" className={label}>Chatbots</text>
        <text x="718" y="258" textAnchor="middle" className={sub}>1&amp;1-KIM-Chat · Impulse-Chat</text>
      </g>
      {/* Mensch */}
      <g>
        <rect x="856" y="150" width="84" height="94" rx="8" className={`${box} help-map-human`} />
        <text x="898" y="186" textAnchor="middle" className={label}>Mensch</text>
        <text x="898" y="206" textAnchor="middle" className={sub}>prüft und</text>
        <text x="898" y="222" textAnchor="middle" className={sub}>entscheidet</text>
      </g>
      <g className="help-map-arrow">
        <path d="M828 150 H852" />
        <path d="M828 242 H852" />
      </g>
      {/* Rückweg gestrichelt */}
      <g className="help-map-arrow help-map-dashed">
        <path d="M898 244 V376 H120 V356" />
      </g>
      <text x="500" y="392" textAnchor="middle" className={sub}>
        Zurück in Quellsysteme wird NUR mit ausdrücklicher menschlicher Freigabe geschrieben (heute: dokumentierter Probelauf)
      </text>
    </svg>
  );
}

export default function HelpPage() {
  const location = useLocation();
  const { perspective } = usePerspective();
  const istKundensicht = perspective === "kunde";

  const [query, setQuery] = useState("");
  const [role, setRole] = useState<RoleFilter>("alle");
  const [lightbox, setLightbox] = useState<HelpImage | null>(null);
  const [openModule, setOpenModule] = useState<Record<string, boolean>>({});
  const moduleRefs = useRef<Record<string, HTMLElement | null>>({});

  const search = normalize(query.trim());

  /* Kontexthilfe: /app/hilfe#modul-<id> öffnet und scrollt zum Modul. */
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const id = hash.startsWith("modul-") ? hash.slice("modul-".length) : null;
    if (id && MODULE_HELP.some((m) => m.id === id)) {
      setOpenModule((prev) => ({ ...prev, [id]: true }));
      window.setTimeout(() => {
        moduleRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else {
      window.setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  /* Kundensicht: nur freigegebene Routen; Rollenfilter "berater" gibt es dort nicht. */
  useEffect(() => {
    if (istKundensicht && role === "berater") setRole("alle");
  }, [istKundensicht, role]);

  const rollenOptionen: Array<{ id: RoleFilter; label: string }> = useMemo(() => {
    const basis: Array<{ id: RoleFilter; label: string }> = [{ id: "alle", label: "Alle" }];
    (istKundensicht ? (["fachbereich", "management"] as HelpAudience[]) : (["fachbereich", "management", "berater"] as HelpAudience[])).forEach(
      (a) => basis.push({ id: a, label: AUDIENCE_LABEL[a] }),
    );
    return basis;
  }, [istKundensicht]);

  const sichtbareGuides = useMemo(
    () => ROLLEN_GUIDES.filter((g) => (istKundensicht ? g.id !== "berater" : true)),
    [istKundensicht],
  );

  const sichtbareModule = useMemo(() => {
    return MODULE_HELP.filter((m) => {
      if (istKundensicht && !KUNDE_ALLOW.has(m.route)) return false;
      if (role !== "alle" && !m.audience.includes(role)) return false;
      if (search && !modulHaystack(m).includes(search)) return false;
      return true;
    });
  }, [istKundensicht, role, search]);

  const sichtbareSysteme = useMemo(
    () => SYSTEME.filter((s) => (istKundensicht ? !s.beraterOnly : true)),
    [istKundensicht],
  );

  const sichtbareAgenten = useMemo(
    () => AGENTEN.filter((a) => (istKundensicht ? !a.beraterOnly : true)),
    [istKundensicht],
  );

  const sichtbarePortale = useMemo(
    () => PORTALE.filter((p) => (istKundensicht ? p.zielgruppe === "kunde" : true)),
    [istKundensicht],
  );

  const sichtbareFaq = useMemo(() => {
    if (!search) return FAQ;
    return FAQ.filter((f) => normalize(`${f.frage} ${f.antwort}`).includes(search));
  }, [search]);

  const sichtbaresGlossar = useMemo(() => {
    const eintraege = Object.entries(GLOSSAR);
    if (!search) return eintraege;
    return eintraege.filter(([, e]) => normalize(`${e.titel} ${e.kurz} ${e.lang ?? ""}`).includes(search));
  }, [search]);

  const suchtreffer = search ? sichtbareModule.length + sichtbareFaq.length + sichtbaresGlossar.length : null;

  return (
    <section className="help-page" data-help-version="v140">
      <header className="help-hero">
        <p className="hero-eyebrow">Hilfe-Center</p>
        <h1>Diese Plattform verstehen und sicher nutzen</h1>
        <p className="hero-sub">
          Für <strong>Berater:innen</strong>, <strong>1&amp;1-Fachbereiche</strong> und <strong>Management</strong>:
          Was jedes Modul zeigt, was Sie dort tun können, was im Hintergrund passiert — und wo die Anwendung
          bewusst stoppt, wenn Daten oder Freigaben fehlen.
        </p>
        <div className="help-hero-meta" aria-label="Geltungsbereich dieser Hilfe">
          <span>Stand 21.07.2026</span>
          <span>Beschreibt nur real vorhandene Funktionen</span>
          <span>Abbildungen: synthetische Übungsdaten (Sandbox)</span>
        </div>
      </header>

      <div className="help-tools" aria-label="Hilfe filtern">
        <label className="help-search">
          <span>Hilfe durchsuchen</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Zum Beispiel: Kennzahl fehlt, Kanban, E-Mail, PASS …"
          />
        </label>
        <div className="help-role-filter" role="group" aria-label="Inhalte nach Rolle filtern">
          {rollenOptionen.map((option) => (
            <button
              key={option.id}
              type="button"
              className={role === option.id ? "active" : ""}
              aria-pressed={role === option.id}
              onClick={() => setRole(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {suchtreffer !== null && (
        <p className="help-search-summary" role="status">
          {suchtreffer} Treffer in Modulen, Fragen und Glossar
        </p>
      )}

      <nav className="help-jump" aria-label="Abschnitte dieser Hilfeseite">
        <a href="#einstieg">Einstieg nach Rolle</a>
        <a href="#module">Module A–Z</a>
        <a href="#zusammenspiel">Zusammenwirken</a>
        <a href="#agenten">Agenten-Landkarte</a>
        <a href="#rollen">Wer darf was</a>
        <a href="#faq">Häufige Fragen</a>
        <a href="#melden">Fehler melden</a>
        <a href="#funktionsstand">Funktionsstand</a>
        <a href="#glossar">Glossar</a>
        <a href="#portale">Doku-Portale</a>
      </nav>

      {/* 1) EINSTIEG NACH ROLLE */}
      <section className="help-section" id="einstieg">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Einstieg</p>
            <h2>Was ist das hier — für meine Rolle?</h2>
          </div>
          <p>
            Wählen Sie Ihre Rolle: Jede Karte erklärt in einfacher Sprache, wozu die Plattform für Sie da ist,
            was Ihre fünf wichtigsten Aufgaben sind und wo Sie zuerst klicken.
          </p>
        </div>
        <div className="help-rollen-grid">
          {sichtbareGuides.map((guide) => (
            <article
              key={guide.id}
              className={`help-rolle ${role === guide.id ? "active" : ""}`}
              id={`rolle-${guide.id}`}
            >
              <header>
                <h3>{guide.titel}</h3>
                <p className="help-rolle-sub">{guide.untertitel}</p>
              </header>
              <p className="help-rolle-intro">{guide.wasIstDas}</p>
              <strong className="help-rolle-caption">Meine 5 wichtigsten Aufgaben</strong>
              <ol>
                {guide.aufgaben.map((a) => (
                  <li key={a.text}>
                    {a.text} <Link to={a.route}>{a.linkLabel}</Link>
                  </li>
                ))}
              </ol>
              <div className="help-rolle-first">
                <strong>Wo klicke ich zuerst?</strong>
                <p>{guide.ersterKlick.text}</p>
                <Link to={guide.ersterKlick.route} className="btn primary small">
                  {guide.ersterKlick.linkLabel}
                </Link>
              </div>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  setRole(guide.id);
                  document.getElementById("module")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Module für diese Rolle anzeigen
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* 2) MODULE */}
      <section className="help-section" id="module">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Modul für Modul</p>
            <h2>Alle Arbeitsbereiche erklärt</h2>
          </div>
          <p>
            {sichtbareModule.length} von {MODULE_HELP.length} Modulen sichtbar (Filter: Rolle{istKundensicht ? ", Kundensicht" : ""}
            {search ? ", Suche" : ""}). Jedes Modul beantwortet dieselben fünf Fragen. Tipp: Das ?-Symbol oben auf
            jeder Seite führt direkt zum passenden Eintrag hier.
          </p>
        </div>

        {sichtbareModule.length === 0 ? (
          <div className="help-empty" role="status">
            <strong>Kein Modul passt zu Filter oder Suche.</strong>
            <p>Suchbegriff kürzen oder den Rollenfilter auf „Alle" stellen.</p>
            <button type="button" className="btn ghost" onClick={() => { setQuery(""); setRole("alle"); }}>
              Filter zurücksetzen
            </button>
          </div>
        ) : (
          <div className="help-mod-list">
            {sichtbareModule.map((m) => {
              const offen = !!openModule[m.id] || !!search;
              return (
                <details
                  key={m.id}
                  id={`modul-${m.id}`}
                  className="help-mod"
                  open={offen}
                  ref={(el) => { moduleRefs.current[m.id] = el; }}
                  onToggle={(event) => {
                    const isOpen = (event.currentTarget as HTMLDetailsElement).open;
                    setOpenModule((prev) => (prev[m.id] === isOpen ? prev : { ...prev, [m.id]: isOpen }));
                  }}
                >
                  <summary>
                    <span className="help-mod-title">
                      <strong>{m.titel}</strong>
                      <small>{m.kurz}</small>
                    </span>
                    <span className="help-mod-meta">
                      {m.audience.length === 3 ? <em>alle Rollen</em> : m.audience.map((a) => <em key={a}>{AUDIENCE_LABEL[a]}</em>)}
                    </span>
                  </summary>
                  <div className="help-mod-body">
                    <div className="help-mod-grid">
                      <div className="help-mod-block">
                        <h4>Was sehe ich hier?</h4>
                        <ul>{m.sehen.map((s) => <li key={s}>{s}</li>)}</ul>
                      </div>
                      <div className="help-mod-block">
                        <h4>Was kann ich tun?</h4>
                        <ol>{m.tun.map((s) => <li key={s}>{s}</li>)}</ol>
                      </div>
                      <div className="help-mod-block">
                        <h4>Was passiert im Hintergrund?</h4>
                        <p>{m.hintergrund}</p>
                        <h4>Woher kommen die Daten?</h4>
                        <p>{m.daten}</p>
                      </div>
                      <div className="help-mod-block">
                        <h4>Was darf meine Rolle?</h4>
                        <p>{m.rollen}</p>
                        {m.grenzen && (
                          <p className="help-mod-grenze">
                            <strong>Bewusste Grenze:</strong> {m.grenzen}
                          </p>
                        )}
                        <Link to={m.route} className="btn ghost small">Modul öffnen</Link>
                      </div>
                    </div>
                    {m.bilder && m.bilder.length > 0 && (
                      <div className="help-mod-shots">
                        {m.bilder.map((bild) => (
                          <figure className="help-shot" key={bild.src}>
                            <button type="button" onClick={() => setLightbox(bild)} aria-label={`Bild vergrößern: ${bild.beschriftung}`}>
                              <img
                                src={bild.src}
                                alt={bild.alt}
                                loading="lazy"
                                width="1440"
                                height="900"
                                onError={(event) => {
                                  (event.currentTarget.closest("figure") as HTMLElement | null)?.setAttribute("hidden", "hidden");
                                }}
                              />
                            </button>
                            <figcaption>
                              <span>{bild.beschriftung}</span>
                            </figcaption>
                          </figure>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </section>

      {/* 3) ZUSAMMENWIRKEN */}
      <section className="help-section" id="zusammenspiel">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Zusammenwirken</p>
            <h2>Wie die Systeme zusammenspielen</h2>
          </div>
          <p>
            Jede Information hat genau ein führendes System; alles hier ist eine geprüfte Ableitung daraus.
            Die Landkarte zeigt den Weg — die Absätze erklären jedes System in einem Absatz.
          </p>
        </div>
        <div className="help-map">
          <WirkungsLandkarte />
        </div>
        <div className="help-systeme">
          {sichtbareSysteme.map((s) => (
            <article className="help-system" key={s.id} id={`system-${s.id}`}>
              <h3>{s.name}</h3>
              <p>{s.absatz}</p>
              <p className="help-system-merksatz"><strong>Merksatz:</strong> {s.merksatz}</p>
            </article>
          ))}
        </div>
        <div className="help-concept">
          <div className="help-section-head compact">
            <div>
              <p className="help-kicker">Vertiefung</p>
              <h2 id="help-concept-title">Vom Datenbeleg zur Wirkung</h2>
            </div>
          </div>
          <ConceptFlow />
        </div>
      </section>

      {/* 4) AGENTEN */}
      <section className="help-section" id="agenten">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Automatik transparent</p>
            <h2>Agenten-Landkarte: Wer arbeitet wann für Sie?</h2>
          </div>
          <p>
            Alle automatischen Läufe der Plattform — mit echtem Zeitplan. Nichts davon ändert Fremdsysteme;
            alles hinterlässt nachvollziehbare Spuren.
          </p>
        </div>
        <div className="help-table-wrap" tabIndex={0} role="region" aria-label="Tabelle der automatischen Agenten">
          <table className="help-table">
            <thead>
              <tr>
                <th scope="col">Agent</th>
                <th scope="col">Wann</th>
                <th scope="col">Liest</th>
                <th scope="col">Erzeugt</th>
                <th scope="col">Sehe ich wo?</th>
              </tr>
            </thead>
            <tbody>
              {sichtbareAgenten.map((a) => (
                <tr key={a.agent}>
                  <th scope="row">{a.agent}</th>
                  <td>{a.wann}</td>
                  <td>{a.liest}</td>
                  <td>{a.erzeugt}</td>
                  <td>{a.sichtbar}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 5) ROLLEN-MATRIX */}
      <section className="help-section" id="rollen">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Berechtigungen</p>
            <h2>Wer darf was?</h2>
          </div>
          <p>In Alltagssprache — die technische Durchsetzung übernehmen Anmeldung (Rollen) und Mandantentrennung.</p>
        </div>
        <div className="help-table-wrap" tabIndex={0} role="region" aria-label="Rollen-Matrix">
          <table className="help-table">
            <thead>
              <tr>
                <th scope="col">Tätigkeit</th>
                <th scope="col">1&amp;1 Fachbereich</th>
                <th scope="col">Management / Steuerkreis</th>
                <th scope="col">Berater:in (Impulse)</th>
              </tr>
            </thead>
            <tbody>
              {ROLLEN_MATRIX.map((z) => (
                <tr key={z.taetigkeit}>
                  <th scope="row">{z.taetigkeit}</th>
                  <td>{z.fachbereich}</td>
                  <td>{z.management}</td>
                  <td>{z.berater}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="help-hinweise">
          {ROLLEN_HINWEISE.map((h) => <li key={h}>{h}</li>)}
        </ul>
      </section>

      {/* 6) FAQ */}
      <section className="help-section" id="faq">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Diagnose</p>
            <h2>Häufige Fragen</h2>
          </div>
          <p>{sichtbareFaq.length} von {FAQ.length} Fragen{search ? " passend zur Suche" : ""}.</p>
        </div>
        <div className="help-faq">
          {sichtbareFaq.map((item) => (
            <details key={item.frage} open={!!search}>
              <summary>{item.frage}</summary>
              <p>{item.antwort}</p>
              {item.route && item.linkLabel && <Link to={item.route}>{item.linkLabel}</Link>}
            </details>
          ))}
        </div>
      </section>

      {/* 7) MELDEN */}
      <section className="help-section" id="melden">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Wenn etwas fehlt oder falsch ist</p>
            <h2>Problem melden — nicht umgehen</h2>
          </div>
          <p>{MELDEWEG.einleitung}</p>
        </div>
        <div className="help-melden-grid">
          <div className="help-mod-block">
            <h4>Gutes Fehlerbild (Checkliste)</h4>
            <ul>{MELDEWEG.checkliste.map((c) => <li key={c}>{c}</li>)}</ul>
            <p className="muted small">{MELDEWEG.kontakt}</p>
          </div>
          <div className="help-mod-block">
            <h4>Drei Grundsätze</h4>
            <ul>{MELDEWEG.grundsaetze.map((g) => <li key={g}>{g}</li>)}</ul>
            <div className="help-contact-actions">
              <Link to="/app/agents" className="btn ghost small">Agent Runs (Run-ID finden)</Link>
              <Link to="/app/benachrichtigungen" className="btn ghost small">Benachrichtigungen</Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8) FUNKTIONSSTAND */}
      <section className="help-section" id="funktionsstand">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Transparenz</p>
            <h2>Was heute wirklich läuft</h2>
          </div>
          <p>Funktionen ohne abgesicherte Verarbeitung bleiben sichtbar als „teilweise" oder „nicht aktiv" markiert.</p>
        </div>
        <ul className="help-features">
          {FUNKTIONSSTAND.map((feature) => (
            <li key={feature.name} className={`help-feature stand-${feature.stand}`}>
              <span className="help-badge">{STATUS_LABEL[feature.stand]}</span>
              <div>
                <strong>{feature.name}</strong>
                <p>{feature.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* 9) GLOSSAR */}
      <section className="help-section" id="glossar">
        <div className="help-section-head">
          <div>
            <p className="help-kicker">Begriffe</p>
            <h2>Glossar</h2>
          </div>
          <p>{sichtbaresGlossar.length} Begriffe{search ? " passend zur Suche" : ""}.</p>
        </div>
        {sichtbaresGlossar.length > 0 ? (
          <dl className="help-glossar">
            {sichtbaresGlossar.map(([key, entry]) => (
              <div key={key} className="help-glossar-item">
                <dt>{entry.titel}</dt>
                <dd>
                  <p>{entry.kurz}</p>
                  {entry.lang && <p className="muted small">{entry.lang}</p>}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="help-empty">Kein Glossarbegriff passt zur aktuellen Suche.</p>
        )}
      </section>

      {/* 10) PORTALE */}
      <section className="help-section help-contact" id="portale">
        <div>
          <p className="help-kicker">Weiterführend</p>
          <h2>Doku-Portale und Nachbar-Systeme</h2>
          <p>
            Vertiefende Dokumentation lebt außerhalb der App — passend zu Ihrer Rolle. Alle Links öffnen in einem neuen Tab.
          </p>
          <ul className="help-portale">
            {sichtbarePortale.map((p) => (
              <li key={p.url}>
                <a href={p.url} target="_blank" rel="noreferrer noopener">{p.name}</a>
                <span>{p.zweck}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="help-contact-actions">
          <Link to="/app/start" className="btn primary">Zur Startübersicht</Link>
        </div>
      </section>

      {lightbox && (
        <div
          className="help-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.beschriftung}
          onClick={() => setLightbox(null)}
        >
          <div className="help-lightbox-inner" onClick={(event) => event.stopPropagation()}>
            <header>
              <div>
                <strong>Bild in Groß</strong>
                <span>{lightbox.beschriftung}</span>
              </div>
              <button type="button" onClick={() => setLightbox(null)} aria-label="Bild schließen" title="Schließen">×</button>
            </header>
            <img src={lightbox.src} alt={lightbox.alt} width="1440" height="900" />
          </div>
        </div>
      )}
    </section>
  );
}
